/**
 * @file hooks/useFetch
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import {useState, useEffect, useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {isFunction} from 'lodash';
import {useOriginalDeepCopy} from '@huse/previous-value';

export interface FetchPayload {
    needLoading?: boolean; // 是否需要Loading，默认为true（一般轮询时会设置为false）
    extra?: any;
}

export type FetchParams = boolean | FetchPayload;

export interface FetchResult<TParam, TData> {
    loading: boolean;
    loaded: boolean;
    reload: (reloadPayload?: FetchParams) => Promise<any>;
    data?: TData | null;
    error?: Error | null;
    latestPayload?: TParam;
};

const createCustomDispatchHook = action => {
    // zhangzhe 2020-03-09 为了兼容没有通过bindActionCreators传来的action
    // FIXME: 这里有更好的实现么？硬编码不太合适
    const actionString = action.toString();
    const withDispatch = /dispatch\(/.test(actionString) && !/actionCreator/.test(actionString);
    return withDispatch ? useDispatch : () => {};
};

const useFetch = <TParam, TData>(
    action: (params?: any) => Promise<TData>,
    params?: TParam,
    $onRequest?: (payload?: any, extra?: any) => any,
    $onResponse?: (result?: any, params?: any) => any,
    $onError?: (ex: any) => void,
    abort?: boolean
): FetchResult<TParam, TData> => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<Error | null>();
    const [data, setData] = useState<TData | null>();
    const [latestPayload, setLatestPayload] = useState<TParam>();

    const originalParams = useOriginalDeepCopy(params);
    // zhangzhe 2019-12-13 三个回调函数放在refs中 不能用useEffect来监听更新，因为可能出现来不及的情况
    // xuemin02 2022-06-28 在调用reload时，会出现params不是最新的问题
    const hooksRef = useRef<any>(null);
    hooksRef.current = {
        params,
        $onRequest,
        $onResponse,
        $onError
    };
    const useCustomDispatch = createCustomDispatchHook(action);
    const dispatch = useCustomDispatch() as any;

    const runFetch = useCallback(async (fetchParams?: FetchParams) => {
        if (typeof fetchParams === 'boolean') {
            fetchParams = {needLoading: fetchParams};
        }
        const {needLoading = true, extra} = fetchParams || {};
        const {params, $onRequest} = hooksRef.current;
        try {
            if (abort) {
                return;
            }
            if (needLoading) {
                setLoading(true);
            }
            setLoaded(false);
            setError(null);
            // 发起前的钩子
            const requestPayload = isFunction($onRequest)
                ? ($onRequest(params, extra) || params)
                : params;
            setLatestPayload(requestPayload);
            const result = dispatch ? await dispatch(action(requestPayload)) : await action(requestPayload);

            // 发起后的钩子
            const {$onResponse} = hooksRef.current;
            const resultData = isFunction($onResponse)
                ? ($onResponse(result, requestPayload) || result)
                : result;
            setData(resultData);
            setLoading(false);
            setLoaded(true);
            return result;
        }
        catch (ex: any) {
            // 错误回调
            const {$onError} = hooksRef.current;
            if (isFunction($onError)) {
                $onError(ex);
            }
            setLoading(false);
            setLoaded(true);
            setError(ex);
        }
    }, [action, abort, dispatch]);

    // 参数和refreshFlag一改动则重新请求接口
    useEffect(() => {
        runFetch();
    }, [runFetch, originalParams]);

    return {
        loading,
        loaded,
        data,
        error,
        reload: runFetch,
        latestPayload
    };
};

export default useFetch;
